<template>
  <div class="inventory">
    <van-nav-bar title="车辆盘点" left-arrow @click-left="$router.back()" />

    <!-- 搜索栏 -->
    <div class="search-bar">
      <van-search v-model="searchText" placeholder="搜索车架号/公司" shape="round" />
    </div>

    <!-- 待盘点列表 -->
    <div class="vehicle-list">
      <div
        class="vehicle-card"
        v-for="item in filteredList"
        :key="item.no"
        @click="startCheck(item)"
      >
        <div class="vehicle-info">
          <div class="vehicle-vid">{{ item.vid }}</div>
          <div class="vehicle-detail">
            {{ item.vehicleBand || '' }} {{ item.vehicleModel || '' }}
          </div>
          <div class="vehicle-company">{{ item.ownerCompany || '未知公司' }}</div>
          <div class="vehicle-location">地点：{{ item.storageLocation || '-' }}</div>
        </div>
        <div class="vehicle-action">
          <van-button type="primary" size="small">盘点</van-button>
        </div>
      </div>

      <div v-if="!loading && taskList.length === 0" class="empty-tip">
        暂无待盘点任务
      </div>
    </div>

    <!-- 盘点弹窗 -->
    <van-dialog
      v-model="showCheckDialog"
      title="车辆盘点"
      show-cancel-button
      :before-close="beforeDialogClose"
    >
      <div class="check-content">
        <div class="check-info">
          <div class="info-row">
            <span class="label">车架号：</span>
            <span>{{ currentTask.vid }}</span>
          </div>
          <div class="info-row">
            <span class="label">车辆：</span>
            <span>{{ currentTask.vehicleBand }} {{ currentTask.vehicleModel }}</span>
          </div>
          <div class="info-row">
            <span class="label">公司：</span>
            <span>{{ currentTask.ownerCompany || '未知' }}</span>
          </div>
        </div>

        <!-- 视频上传 -->
        <div class="video-upload">
          <div class="upload-tip">请拍摄车架号视频（不超过100MB）</div>
          <input
            type="file"
            ref="videoInput"
            accept="video/*"
            capture="environment"
            @change="onVideoSelected"
            style="display: none"
          />
          <van-button
            type="primary"
            block
            @click="$refs.videoInput.click()"
            v-if="!selectedVideo"
          >
            选择/录制视频
          </van-button>
          <div v-else class="video-preview">
            <div class="video-name">{{ selectedVideo.name }}</div>
            <div class="video-size">{{ formatSize(selectedVideo.size) }}</div>
            <van-button plain type="danger" size="small" @click="clearVideo">
              重新选择
            </van-button>
          </div>
        </div>

        <!-- 备注 -->
        <van-field
          v-model="checkRemark"
          type="textarea"
          placeholder="备注（选填）"
          rows="2"
          maxlength="200"
          show-word-limit
        />
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { Toast } from "vant";
import http from "../utils/http";

export default {
  name: "VehicleInventory",
  data() {
    return {
      searchText: "",
      taskList: [],
      loading: false,
      showCheckDialog: false,
      currentTask: {},
      selectedVideo: null,
      checkRemark: "",
      uploading: false,
    };
  },
  computed: {
    filteredList() {
      if (!this.searchText) return this.taskList;
      const keyword = this.searchText.toLowerCase();
      return this.taskList.filter(
        (item) =>
          (item.vid && item.vid.toLowerCase().includes(keyword)) ||
          (item.ownerCompany && item.ownerCompany.toLowerCase().includes(keyword))
      );
    },
  },
  created() {
    this.loadTasks();
  },
  methods: {
    async loadTasks() {
      this.loading = true;
      try {
        const res = await http.get("/api/Inventory/GetPendingTasks");
        if (res.code === 0) {
          this.taskList = res.data || [];
        }
      } catch (error) {
        console.error("加载任务失败:", error);
      } finally {
        this.loading = false;
      }
    },
    startCheck(item) {
      this.currentTask = item;
      this.selectedVideo = null;
      this.checkRemark = "";
      this.showCheckDialog = true;
    },
    onVideoSelected(e) {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 100 * 1024 * 1024) {
          Toast("视频不能超过 100MB");
          return;
        }
        this.selectedVideo = file;
      }
    },
    clearVideo() {
      this.selectedVideo = null;
      this.$refs.videoInput.value = "";
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    async beforeDialogClose(action, done) {
      if (action === "confirm") {
        if (!this.selectedVideo) {
          Toast("请先选择视频文件");
          done(false);
          return;
        }
        if (this.uploading) {
          done(false);
          return;
        }
        await this.submitCheck();
        done(false);
      } else {
        done();
      }
    },
    async submitCheck() {
      this.uploading = true;
      Toast.loading({ message: "上传中...", forbidClick: true, duration: 0 });

      try {
        const formData = new FormData();
        formData.append("recordId", this.currentTask.no);
        formData.append("video", this.selectedVideo);
        formData.append("remark", this.checkRemark);

        const res = await http.post("/api/Inventory/UploadAndCheck", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        });

        Toast.clear();

        if (res.code === 0) {
          Toast.success("盘点成功！");
          this.showCheckDialog = false;
          this.loadTasks(); // 刷新列表
        } else {
          Toast.fail(res.message || "盘点失败");
        }
      } catch (error) {
        Toast.clear();
        Toast.fail("上传失败，请重试");
        console.error("上传失败:", error);
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.inventory {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.search-bar {
  padding: 8px 16px;
  background: #fff;
}

.vehicle-list {
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
}

.vehicle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.vehicle-card:active {
  background: #f8f8f8;
}

.vehicle-info {
  flex: 1;
}

.vehicle-vid {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.vehicle-detail {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.vehicle-company {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.vehicle-location {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.vehicle-action {
  margin-left: 12px;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

/* 盘点弹窗样式 */
.check-content {
  padding: 16px;
}

.check-info {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-row {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  color: #999;
}

.video-upload {
  margin-bottom: 16px;
}

.upload-tip {
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
  text-align: center;
}

.video-preview {
  background: #f0f9ff;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}

.video-name {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.video-size {
  font-size: 12px;
  color: #999;
  margin: 4px 0 8px;
}
</style>
